// galleryThemes.ts
import { contrastRatio } from '../generator/contrast';
import { oklchHex, rgbToOklch } from '../generator/oklch';
import { RADIUS_TOKEN_DEFAULTS } from '../catalog/tokenCatalog';
import type { TokenMap } from '../generator/types';
import type { NamedBrand } from '../appearance/defaultAppearance';
import type { RuntimeTypeConfig } from '../runtime/emitRuntimeTypeCss';

/**
 * Complete looks taken from the Formica schema gallery (I-2009): colours,
 * typeface, type sizes and corner radius per scheme. Each scheme is authored
 * for ONE mode (`mode`) — the gallery shows it that way. Its colours are
 * pinned as sparse overrides into that mode's slot on top of the ramp the
 * seeds generate; the other mode is the plain ramp, so the global light/dark
 * toggle keeps working for every scheme.
 *
 * Sizes land on the gallery's absolute px. At scale 1 nx renders body text
 * and table cells both at 14px (`font-size-sm` on a 16px root): body →
 * `type.scale` (body / 14), table → the `table` scope (table / (14 × scale),
 * so S1's 13px table is 0.93), title → `font-size-2xl` (title / (16 × scale)).
 * The gallery's nav text is 13px for every scheme, nx's is 16px (`font-size-md`)
 * → the `navbar` scope. Rounded down: the looks should rather err compact.
 * These px are measured on the former 16px root. Since I-2820 the root is 14px,
 * so every gallery look renders a step smaller, exactly like the default.
 *
 * A `transparent` table header in the gallery is stored as the surface colour
 * it sits on: nx table headers are sticky and must stay opaque.
 */
export type GalleryTheme = {
  id: NamedBrand;
  labelKey: string;
  mode: 'light' | 'dark';
  colors: {
    bg: string;
    surface: string;
    ink: string;
    muted: string;
    accent: string;
    onAccent: string;
    top: string;
    topInk: string;
    topActive: string;
    line: string;
    headBg: string;
    headInk: string;
    zebra: string;
    hover: string;
    field: string;
    /** The soft focus ring, verbatim (`--focus-ring`). */
    focus: string;
    /** Warning badge fill + text (`--warn-bg` / `--warn-ink`). */
    warnBg: string;
    warnInk: string;
    /** Underline of the active nav item, when the gallery draws one. */
    navAccent?: string;
  };
  /** Status seeds (hex, hue source for the ramp); missing ones stay the platform defaults. */
  status: { warning: string; danger?: string; success?: string };
  font: Pick<RuntimeTypeConfig, 'font' | 'customFont'>;
  /** Gallery sizes in px. */
  px: { body: number; table: number; title: number };
  /** Button radius in px; the gallery default is 8px (= nx factor 1). */
  radius: number;
};

/** nx at scale 1: body and table text are `font-size-sm` (14px), nav text is
 *  `font-size-md` (16px); the button radius is 8px. */
const BASE_PX = { body: 14, table: 14, title: 24, radius: 8, nav: 16 } as const;
/** The gallery's nav bar text, the same in every scheme. */
const GALLERY_NAV_PX = 13;
const TITLE_REM = 1.5;

const round = (value: number, step: number): number =>
  Number((Math.round(value / step) * step).toFixed(4));

function alpha(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

/** Same hue + chroma, lightness moved by `dL` (gamut-clipped). */
function shade(hex: string, dL: number): string {
  const { L, C, H } = rgbToOklch(hex);
  return oklchHex(Math.min(0.98, Math.max(0.05, L + dL)), C, H);
}

/**
 * The gallery's accent as a fill that keeps AA 4.5 against its text colour:
 * move lightness away from the text until it passes (amber, orange and lilac
 * with white text do not pass as drawn).
 */
function aaFill(accent: string, onAccent: string): string {
  const darkText = rgbToOklch(onAccent).L < 0.5;
  let fill = accent;
  for (let i = 0; i < 40 && contrastRatio(onAccent, fill) < 4.5; i += 1) {
    fill = shade(fill, darkText ? 0.01 : -0.01);
  }
  return fill;
}

export function galleryRoleOverrides(theme: GalleryTheme): TokenMap {
  const c = theme.colors;
  const fill = aaFill(c.accent, c.onAccent);
  // Hover/active go deeper in light mode, brighter in dark mode (like the ramp).
  const dir = theme.mode === 'light' ? -1 : 1;
  const hover = shade(fill, dir * 0.05);
  const active = shade(fill, dir * 0.1);
  const warn = aaFill(c.warnBg, c.warnInk);
  return {
    'bg-app': c.bg,
    'surface-sunken-bg': c.bg,
    'surface-bg': c.surface,
    'surface-raised-bg': c.surface,
    fg: c.ink,
    'fg-muted': c.muted,
    border: c.line,
    'input-bg': c.field,
    'brand-bg': fill,
    'brand-bg-hover': hover,
    'brand-bg-active': active,
    'brand-bg-focus': fill,
    'brand-fg': c.onAccent,
    'brand-fg-hover': c.onAccent,
    'brand-border': fill,
    'brand-border-hover': hover,
    'brand-border-active': active,
    'focus-ring': fill,
    'focus-ring-soft': c.focus,
    'warning-bg': warn,
    'warning-bg-hover': shade(warn, dir * 0.04),
    'warning-bg-active': shade(warn, dir * 0.08),
    'warning-bg-focus': warn,
    'warning-fg': c.warnInk,
    'warning-fg-hover': c.warnInk,
    link: fill,
    'link-hover': hover,
    'link-active': active,
    'nav-bg': c.top,
    'nav-fg': c.topInk,
    'nav-fg-muted': alpha(c.topInk, 0.72),
    'nav-hover-bg': alpha(c.topInk, 0.08),
    'nav-active-bg': c.topActive,
    ...(c.navAccent ? { 'nav-accent': c.navAccent } : {}),
    'table-header-bg': c.headBg,
    'table-header-fg': c.headInk,
    'table-row-bg': c.surface,
    'table-zebra-bg': c.zebra,
    'table-row-hover-bg': c.hover,
    'table-border': c.line,
  };
}

export function gallerySharedOverrides(theme: GalleryTheme): TokenMap {
  const out: TokenMap = {};
  const factor = Math.min(2, round(theme.radius / BASE_PX.radius, 0.125));
  if (factor !== 1) {
    for (const key of ['radius-sm', 'radius-md', 'radius-lg', 'radius-xl']) {
      const em = Number(/^([\d.]+)em$/.exec(RADIUS_TOKEN_DEFAULTS[key])![1]);
      out[key] = `${round(em * factor, 0.001)}em`;
    }
    if (factor === 0) out['radius-full'] = '0px';
  }
  const titleRatio = theme.px.title / theme.px.body / (BASE_PX.title / BASE_PX.body);
  const title = round(TITLE_REM * titleRatio, 0.0625);
  if (title !== TITLE_REM) out['font-size-2xl'] = `${title}rem`;
  return out;
}

export function galleryType(theme: GalleryTheme): RuntimeTypeConfig {
  const scale = round(theme.px.body / BASE_PX.body, 0.025);
  const table = round(theme.px.table / (BASE_PX.table * scale), 0.01);
  const navbar = Math.floor((GALLERY_NAV_PX / (BASE_PX.nav * scale)) * 100) / 100;
  const scopes = {
    ...(navbar !== 1 ? { navbar } : {}),
    ...(table !== 1 ? { table } : {}),
  };
  return {
    ...theme.font,
    mono: 'system',
    scale: Math.round(scale * 1000) / 1000,
    ...(Object.keys(scopes).length ? { scopes } : {}),
  };
}

const segoe = { font: 'custom', customFont: "'Segoe UI', system-ui, sans-serif" } as const;

const workbench = {
  bg: '#F6F5F2',
  surface: '#FFFFFF',
  ink: '#23282D',
  muted: '#6E7681',
  accent: '#0E5E6F',
  onAccent: '#ffffff',
  line: '#E8E6E1',
  zebra: '#F1EFEA',
  field: '#FDFCFB',
  focus: 'rgba(14, 94, 111, 0.2)',
  warnBg: '#F7ECDA',
  warnInk: '#9A5B00',
} as const;

const workbenchStatus = { warning: '#9A5B00', danger: '#A63A2E', success: '#2F6B3C' };

export const GALLERY_THEMES: readonly GalleryTheme[] = [
  {
    id: 'blp-ci',
    labelKey: 'connect.settings.appearance.brandBlpCi',
    mode: 'light',
    colors: {
      bg: '#F4F8FB',
      surface: '#ffffff',
      ink: '#1c2733',
      muted: '#5c6d7d',
      // One blue everywhere: buttons, links, focus and chips take the navbar's
      // CI blue, not a lighter accent of their own (I-2154).
      accent: '#004268',
      onAccent: '#ffffff',
      top: '#004268',
      topInk: '#ffffff',
      topActive: 'rgba(255, 255, 255, 0.16)',
      line: '#d7e2ec',
      headBg: '#e7f0f8',
      headInk: '#004268',
      zebra: '#eef5fa',
      hover: '#dcebf6',
      field: '#ffffff',
      focus: 'rgba(0, 66, 104, 0.22)',
      warnBg: '#b45309',
      warnInk: '#ffffff',
    },
    status: { warning: '#b45309' },
    font: segoe,
    px: { body: 14, table: 13, title: 24 },
    radius: 8,
  },
  {
    id: 'signal-red',
    labelKey: 'connect.settings.appearance.brandSignalRed',
    mode: 'light',
    colors: {
      bg: '#ffffff',
      surface: '#f7f7f8',
      ink: '#131821',
      muted: '#646973',
      accent: '#EC0016',
      onAccent: '#ffffff',
      top: '#EC0016',
      topInk: '#ffffff',
      topActive: 'rgba(255, 255, 255, 0.22)',
      line: '#d7dce1',
      headBg: '#EC0016',
      headInk: '#ffffff',
      zebra: '#f2f3f5',
      hover: '#fde8ea',
      field: '#ffffff',
      focus: 'rgba(236, 0, 22, 0.2)',
      warnBg: '#F8AB37',
      warnInk: '#131821',
    },
    status: { warning: '#F8AB37' },
    font: { font: 'custom', customFont: 'Helvetica, Arial, sans-serif' },
    px: { body: 14, table: 13, title: 26 },
    radius: 4,
  },
  {
    id: 'signal-yellow',
    labelKey: 'connect.settings.appearance.brandSignalYellow',
    mode: 'light',
    colors: {
      bg: '#fffdf5',
      surface: '#ffffff',
      ink: '#3f3f3f',
      muted: '#7a7a72',
      accent: '#DD1D21',
      onAccent: '#ffffff',
      top: '#FBCE07',
      topInk: '#3f3f3f',
      topActive: 'rgba(221, 29, 33, 0.14)',
      line: '#e8e2cf',
      headBg: '#FBCE07',
      headInk: '#3f3f3f',
      zebra: '#fdf7e3',
      hover: '#faeecb',
      field: '#ffffff',
      focus: 'rgba(221, 29, 33, 0.18)',
      warnBg: '#DD1D21',
      warnInk: '#ffffff',
    },
    status: { warning: '#DD1D21' },
    font: { font: 'custom', customFont: "'Trebuchet MS', Verdana, sans-serif" },
    px: { body: 14, table: 13, title: 25 },
    radius: 20,
  },
  {
    id: 'northern-lights',
    labelKey: 'connect.settings.appearance.brandNorthernLights',
    mode: 'dark',
    colors: {
      bg: '#0f1722',
      surface: '#18232f',
      ink: '#dbe6f1',
      muted: '#8298ac',
      accent: '#4cc3ff',
      onAccent: '#06121d',
      top: '#0a1119',
      topInk: '#dbe6f1',
      topActive: 'rgba(76, 195, 255, 0.18)',
      line: '#2a3a4c',
      headBg: '#1f2d3c',
      headInk: '#9fc9e8',
      zebra: '#141f2b',
      hover: '#22354a',
      field: '#101a25',
      focus: 'rgba(76, 195, 255, 0.25)',
      warnBg: '#e8a13c',
      warnInk: '#1c1305',
    },
    status: { warning: '#e8a13c' },
    font: { font: 'custom', customFont: "system-ui, 'Segoe UI', sans-serif" },
    px: { body: 14, table: 13, title: 23 },
    radius: 8,
  },
  {
    id: 'editorial',
    labelKey: 'connect.settings.appearance.brandEditorial',
    mode: 'light',
    colors: {
      bg: '#faf7f2',
      surface: '#ffffff',
      ink: '#2e2a25',
      muted: '#7d7468',
      accent: '#7a5c2e',
      onAccent: '#ffffff',
      top: '#41371f',
      topInk: '#f4ecdd',
      topActive: 'rgba(244, 236, 221, 0.18)',
      line: '#e2d9ca',
      headBg: '#ffffff',
      headInk: '#7a5c2e',
      zebra: '#f4efe6',
      hover: '#efe6d5',
      field: '#fdfbf7',
      focus: 'rgba(122, 92, 46, 0.2)',
      warnBg: '#9c3d1e',
      warnInk: '#ffffff',
    },
    status: { warning: '#9c3d1e' },
    font: { font: 'custom', customFont: "Georgia, 'Times New Roman', serif" },
    px: { body: 15, table: 14, title: 28 },
    radius: 6,
  },
  {
    id: 'graphite-amber',
    labelKey: 'connect.settings.appearance.brandGraphiteAmber',
    mode: 'light',
    colors: {
      bg: '#f2f2f0',
      surface: '#ffffff',
      ink: '#26282b',
      muted: '#6f7377',
      accent: '#c47a00',
      onAccent: '#ffffff',
      top: '#26282b',
      topInk: '#f2f2f0',
      topActive: 'rgba(196, 122, 0, 0.35)',
      line: '#d9dadb',
      headBg: '#eceded',
      headInk: '#26282b',
      zebra: '#f6f6f5',
      hover: '#f3ead8',
      field: '#ffffff',
      focus: 'rgba(196, 122, 0, 0.22)',
      warnBg: '#a83232',
      warnInk: '#ffffff',
    },
    status: { warning: '#a83232' },
    font: { font: 'custom', customFont: "Tahoma, 'Segoe UI', sans-serif" },
    px: { body: 13, table: 12, title: 22 },
    radius: 4,
  },
  {
    id: 'pastel',
    labelKey: 'connect.settings.appearance.brandPastel',
    mode: 'light',
    colors: {
      bg: '#f6f5fb',
      surface: '#ffffff',
      ink: '#3c3a52',
      muted: '#8a87a6',
      accent: '#7c6fd6',
      onAccent: '#ffffff',
      top: '#ffffff',
      topInk: '#4a3f8f',
      topActive: '#ece9fb',
      line: '#e4e1f2',
      headBg: '#efecfa',
      headInk: '#4a3f8f',
      zebra: '#f5f3fc',
      hover: '#ece9fb',
      field: '#fbfaff',
      focus: 'rgba(124, 111, 214, 0.22)',
      warnBg: '#e58f6a',
      warnInk: '#ffffff',
    },
    status: { warning: '#e58f6a' },
    font: { font: 'custom', customFont: "Verdana, 'Segoe UI', sans-serif" },
    px: { body: 13, table: 12, title: 21 },
    radius: 24,
  },
  {
    id: 'terminal',
    labelKey: 'connect.settings.appearance.brandTerminal',
    mode: 'dark',
    colors: {
      bg: '#0c1210',
      surface: '#111a17',
      ink: '#c9e8d4',
      muted: '#6f9682',
      accent: '#39d47f',
      onAccent: '#06130c',
      top: '#08100d',
      topInk: '#c9e8d4',
      topActive: 'rgba(57, 212, 127, 0.2)',
      line: '#22362c',
      headBg: '#15221c',
      headInk: '#39d47f',
      zebra: '#0f1815',
      hover: '#1a2a22',
      field: '#0a110e',
      focus: 'rgba(57, 212, 127, 0.25)',
      warnBg: '#d4a139',
      warnInk: '#141003',
    },
    status: { warning: '#d4a139' },
    font: { font: 'custom', customFont: "Consolas, 'Courier New', monospace" },
    px: { body: 13, table: 13, title: 21 },
    radius: 4,
  },
  {
    id: 'nordic',
    labelKey: 'connect.settings.appearance.brandNordic',
    mode: 'light',
    colors: {
      bg: '#ffffff',
      surface: '#ffffff',
      ink: '#1b1b1b',
      muted: '#8b8b8b',
      accent: '#1b1b1b',
      onAccent: '#ffffff',
      top: '#ffffff',
      topInk: '#1b1b1b',
      topActive: '#f0f0f0',
      line: '#e6e6e6',
      headBg: '#ffffff',
      headInk: '#8b8b8b',
      zebra: '#fafafa',
      hover: '#f4f4f4',
      field: '#fcfcfc',
      focus: 'rgba(27, 27, 27, 0.14)',
      warnBg: '#c2571c',
      warnInk: '#ffffff',
    },
    status: { warning: '#c2571c' },
    font: { font: 'custom', customFont: "'Helvetica Neue', Helvetica, Arial, sans-serif" },
    px: { body: 14, table: 13, title: 27 },
    radius: 0,
  },
  {
    id: 'steel-orange',
    labelKey: 'connect.settings.appearance.brandSteelOrange',
    mode: 'light',
    colors: {
      bg: '#e9edf0',
      surface: '#f8fafb',
      ink: '#22313d',
      muted: '#5f7280',
      accent: '#e0611a',
      onAccent: '#ffffff',
      top: '#2f4a5e',
      topInk: '#e9edf0',
      topActive: 'rgba(224, 97, 26, 0.85)',
      line: '#c6d1d9',
      headBg: '#2f4a5e',
      headInk: '#e9edf0',
      zebra: '#eef2f4',
      hover: '#fbe9dd',
      field: '#ffffff',
      focus: 'rgba(224, 97, 26, 0.22)',
      warnBg: '#b7791f',
      warnInk: '#ffffff',
    },
    status: { warning: '#b7791f' },
    font: { font: 'custom', customFont: "Arial, 'Segoe UI', sans-serif" },
    px: { body: 13, table: 12, title: 22 },
    radius: 6,
  },
  {
    id: 'workbench',
    labelKey: 'connect.settings.appearance.brandWorkbench',
    mode: 'light',
    colors: {
      ...workbench,
      top: '#0E5E6F',
      topInk: '#F2F7F8',
      topActive: 'rgba(255, 255, 255, 0.18)',
      headBg: '#FFFFFF',
      headInk: '#6E7681',
      hover: '#E3EEF0',
    },
    status: workbenchStatus,
    font: segoe,
    px: { body: 14, table: 13.5, title: 24 },
    radius: 8,
  },
  {
    id: 'workbench-light',
    labelKey: 'connect.settings.appearance.brandWorkbenchLight',
    mode: 'light',
    colors: {
      ...workbench,
      top: '#F6F5F2',
      topInk: '#23282D',
      topActive: '#E9F1F2',
      headBg: '#FFFFFF',
      headInk: '#6E7681',
      hover: '#E3EEF0',
      navAccent: '#0E5E6F',
    },
    status: workbenchStatus,
    font: segoe,
    px: { body: 14, table: 13.5, title: 24 },
    radius: 8,
  },
  {
    id: 'workbench-calm',
    labelKey: 'connect.settings.appearance.brandWorkbenchCalm',
    mode: 'light',
    colors: {
      ...workbench,
      top: '#F6F5F2',
      topInk: '#23282D',
      topActive: '#E9F1F2',
      headBg: '#F3F1EC',
      headInk: '#55606A',
      hover: '#E3EEF0',
      navAccent: '#0E5E6F',
    },
    status: workbenchStatus,
    font: segoe,
    px: { body: 14, table: 13.5, title: 24 },
    radius: 8,
  },
];
